<template>
  <div class="view-index pc-only">
    <div
      data-desc="for-aegis-fmp"
      AEGIS-FIRST-SCREEN-TIMING
    />
    <banner
      v-show="showBanner"
      v-if="bannerExist"
      :person="bannerData?.person ?? null"
      :banner-txt="bannerData?.bannerTxt ?? ''"
      :btn-txt="bannerData?.btnTxt ?? ''"
      :mini-app-path-click-avator="miniAppPathClickAvator"
      :mini-app-path-click-look="miniAppPathClickLook"
      :exposure-material-info="exposureMaterialInfo"
      :active-feed-report-opt="activeFeedReportOpt"
    />
    <div
      ref="swiperContainerRef"
      class="swiper-container"
    >
      <div class="swiper-wrapper">
        <div
          v-for="(feed, index) in feedsList"
          :key="index"
          class="swiper-slide"
        >
          <swiper-item
            ref="swiperItemRef"
            :feed="feed"
            :is-last-feed="index === feedsList.length - 1"
            :init-play-rec="initPlayRec"
            :show-rec-in-play-page="showRecInPlayPage"
            :strategy-loaded="true"
            :play-icon-click="playIconClick"
            @on-ended="onEnded"
          />
        </div>
      </div>
    </div>
    <collection-container
      v-if="isCollection"
      :show-rec-in-play-page="showRecInPlayPage"
      :active-feed="activeFeed"
      :active-feed-report-opt="activeFeedReportOpt"
    />
    <rec-feeds
      v-show="showRec"
      :show-rec="showRec"
      :installed="isWsInstalled"
      @on-replay="onReplay"
    />
    <error
      v-if="!!errorMsg"
      :msg="errorMsg"
    />
  </div>
  <div class="qrcode-wrap pc-only">
    <img
      class="qrcode"
      src="https://qzonestyle.gtimg.cn/qz-proj/weishi-pc/img/qrcode.png"
    >
    <p class="txt">
      下载客户端
    </p>
  </div>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import Swiper from 'swiper';
import { useStore } from '@/store/index';
import SwiperItem from '@/components/swiper-item.vue';
import CollectionContainer from '@/components/collection-container.vue';
import RecFeeds from '@/components/rec-feeds/index.vue';
import Banner from '@/components/common/banner.vue';
import Error from '@/components/error.vue';
import useGetCommonMethods from '@/composable/use-get-common-method';
import useProvideBrowserInfo from '@/composable/use-provide-browser-info';
import useProvideUrlInfo from '@/composable/use-provide-url-info';
import useProvideCookieInfo from '@/composable/use-provide-cookie-info';
import useInitializeHomeData from '@/composable/use-initialize-home-data';
import useInitializeShareData from '@/composable/use-initialize-share-data';
import useGetTabExperiment from '@/composable/use-get-tab-experiment';
import { EMiniAppPosition } from '@/constants/mini-app';
import { EFeedReserveType } from '@/constants/feed';
import { GROWTH_POPUP } from '@/constants/ab-test';
import { getSwiper, isWSInstall } from '@/utils/script';
import { IS_SSR } from '@/utils/env';
import { openSchema } from '@/utils/schema';
import 'swiper/swiper-bundle.css';

const store = useStore();
const { state } = store;
const { provideInfo: provideUrlInfo, info: urlInfo } = useProvideUrlInfo();
const { provideInfo: provideBrowserInfo, info: browerInfo } = useProvideBrowserInfo();
const { provideInfo: provideCookieInfo } = useProvideCookieInfo();
// 提供utils
const {
  getMiniAppPath,
  getMiniappPlayerFeed,
  openSchemaAppendParamsClipboard,
  openCollectionSchema,
  appendSchema,
} = useGetCommonMethods();
const { getTabExp } = useGetTabExperiment();

const swiper = ref<Swiper>(); // swiper对象，主要用于切换视频
const showRec = ref(false); // 是否展示推荐视频
const isWsInstalled = ref<boolean | null>(null); // 是否安装了微视app  null表示未知
const swiperContainerRef = ref<HTMLElement>();

// 是否初始化banner
const bannerExist = computed(() => {
  const { scene, vtype } = urlInfo.query;
  if (browerInfo.client === 'qq' || vtype === 'interactive') {
    // qq场景默认不展示banner
    return false;
  }
  // scene === 7|infoflow 的场景，属于投放页面情形，不显示banner
  return !(scene === '7' || scene === 'infoflow');
});
const isNewUI = computed(() => state.isNewUI);
const activeIndex = computed(() => state.activeIndex);
const isCollection = computed(() => state.isCollection);
const growthTestData = computed(() => state.growthTestData);
const showCollection = computed(() => state.showCollection);
const feedsList = computed(() => state.feedsList);
const bannerData = computed(() => state.banner);
const errorMsg = computed(() => state.errorMsg);
const isGrowthPopup = computed(() => state.isGrowthPopup);
const showRecInPlayPage = computed(() => !isCollection.value && !!growthTestData.value?.showModule);
const showBanner = computed(() => {
  if (isCollection.value) {
    // 精选集有二种样式: 新UI和旧UI. 旧UI的场景下，精选集和banner是一起展示隐藏的，新UI则完全相反
    return isNewUI.value ? !showCollection.value : showCollection.value;
  }
  return true;
});
const miniAppPathClickAvator = computed(() => getMiniAppPath(EMiniAppPosition.videoHeadpic, {
  ownerid: bannerData.value?.person?.id || '',
}));
const miniAppPathClickLook = computed(() => getMiniAppPath(EMiniAppPosition.open));
const exposureMaterialInfo = computed(() => feedsList.value[activeIndex.value]?.reserve[
  EFeedReserveType.exposureMaterialInfo
]);
const activeFeed = computed(() => (isNewUI.value ? feedsList.value[activeIndex.value] : feedsList.value[0]));
const activeFeedReportOpt = computed(() => ({
  video_id: activeFeed.value?.id ?? '',
  owner_id: activeFeed.value?.poster.id ?? '',
}));

const initPlayRec = false;
const swiperOptions = {
  direction: 'vertical',
  resistanceRatio: 0,
  allowTouchMove: false,
  initialSlide: 0,
};

const initSwiper = async () => {
  const Swiper = await getSwiper();
  if (Swiper) {
    // @ts-ignore
    swiper.value = new Swiper(swiperContainerRef.value, {
      ...swiperOptions,
      initialSlide: activeIndex.value,
    });
  }
};

const studyModeTips = () => {
  window.mqq?.ui.showTips({
    text: '青少年模式下不可访问',
    iconMode: 2,
  });
};

// 最终会传递给video-player组件，点击播放及暂停按钮时调用
const playIconClick = () => {
  const { id: feedId } = urlInfo.query;
  if (
    browerInfo.client === 'wx'
    && isWsInstalled.value === false
    && isGrowthPopup.value === GROWTH_POPUP.YES
  ) {
    openSchema('feed', { feed_id: feedId });
  }
  // 手Q 青少年模式下不可访问
  if (browerInfo.isQQStudyMode) {
    studyModeTips();
    return false;
  }
  return true;
};

const onEnded = () => {
  if (!showCollection.value) {
    showRec.value = true;
  }
};
const onReplay = () => {
  showRec.value = false;
};

const initWsIntalled = async () => {
  if (isWsInstalled.value === null) {
    // 设置2秒的超时时间，防止isWSInstall阻塞导致页面黑屏
    const { id: feedId } = urlInfo.query;
    const schema = `weishi://feed?feed_id=${feedId}&session_from=10000018`;
    const finalSchema = await appendSchema(schema);
    const res = await Promise.race([
      new Promise(resolve => setTimeout(resolve, 2000)),
      isWSInstall(finalSchema),
    ]);
    isWsInstalled.value = !!res;
  }
};

// 微信自动拉活
const autoJumpAppInWX = async () => {
  const { id: feedId } = urlInfo.query;
  if (
    browerInfo.client === 'wx'
    && isWsInstalled.value
    && isGrowthPopup.value === GROWTH_POPUP.YES
  ) {
    openSchemaAppendParamsClipboard(true, 'feed', { feed_id: feedId });
  }
};

// 微博自动拉活
const autoDownloadWSInWeibo = async () => {
  const { id: feedId } = urlInfo.query;
  if (browerInfo.client !== 'wb') {
    return;
  }
  if (isWsInstalled.value === false) {
    openSchemaAppendParamsClipboard(true, 'feed', {
      feed_id: feedId,
    });
  }
};

const openWsInQQ = async () => {
  const {
    id: feedId,
    collectionid: collectionId,
    spid: sharePersonId,
  } = urlInfo.query;
  const feed = feedsList.value[activeIndex.value];
  if (!feed || browerInfo.client !== 'qq') return;
  if (!window) return;
  const { isQQStudyMode } = browerInfo;
  // 启用青少年模式,弹窗提示,并禁止视频的播放,不再拉起微视公众号
  if (isQQStudyMode) {
    studyModeTips();
    return;
  }

  const params = getMiniappPlayerFeed(feed);
  if (params?.videoUrl) {
    const isOwner = feed.posterId === sharePersonId;
    const webTestId = growthTestData.value?.strategyID || '';
    const spid = sharePersonId;
    const scene = isOwner ? 'homepage_main' : 'homepage_guest';
    window?.mqq?.invokeSchema('mqqapi', 'wsgzh', 'miniapp_player', {
      feeds: encodeURIComponent(JSON.stringify(params)),
      scene,
      scenes_from: 'share', // 场景
      action: 'openPlayer', // 行为
      spid,
      collection_id: collectionId || '',
      active_id: '',
      share_scenes_from: 11,
      web_test_id: webTestId,
    });
    window.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        window?.mqq?.invoke('ui', 'popBack');
      }
    });
    return;
  }

  if (isCollection.value) {
    // 跳转到中间页去下载或唤起微视
    openCollectionSchema();
  } else {
    // 直接唤起微视
    openSchema('feed', { feed_id: feedId });
  }
};
// 提供cookie相关信息
provideCookieInfo();
// 提供浏览器平台相关信息
provideBrowserInfo();
// 提供当前页面链接上的相关信息
provideUrlInfo();
// 初始化首页数据
const { initializeHomeData } = useInitializeHomeData();
// 初始化分享相关数据
const initializeShareData = useInitializeShareData();

watch(activeIndex, (val) => {
  if (!IS_SSR && isCollection.value && swiper.value) {
    swiper.value.slideTo(val, 0);
  }
});

onMounted(async () => {
  await initializeHomeData();
  // 当页面数据初始化完成之后
  // await initWsIntalled();
  // await getTabExp(isWsInstalled.value ?? false);
  initializeShareData(); // 初始化分享
  const { collectionid: collectionId, debug } = urlInfo.query;
  // 如果是精选集, 需要初始化swiper实例可切换视频
  if (collectionId) {
    initSwiper();
  }

  autoJumpAppInWX();
  autoDownloadWSInWeibo();
  openWsInQQ();
  if (debug) {
    import('vconsole').then(({ default: VConsole }) => {
      new VConsole();
    });
  }
});
</script>

<style lang="scss">
.view-index {
  position: relative;
  display: flex;
  box-sizing: border-box;
  margin: 0 auto;
  height: 100%;
  color: #fff;
  overflow: hidden;

  .swiper-container {
    width: 100%;
  }

  * {
    box-sizing: inherit;
  }
}
.qrcode-wrap {
  position: absolute;
  @include responsiveStyleSingle(top, 20, "px");
  right: 50%;
  @include responsiveStyleSingle(margin-right, -515, "px");
}

.qrcode {
  @include responsiveStyleSingle(width, 120, "px");
  @include responsiveStyleSingle(height, 120, "px");
}

.qrcode-wrap .txt {
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  @include responsiveStyleSingle(line-height, 25, "px");
  @include responsiveStyleSingle(margin-top, 9, "px");
  @include responsiveStyleSingle(font-size, 14, 'px');
}
</style>
